import * as Msal from '@azure/msal-browser';
import { AuthConfig, LoginRequest } from './AuthConfig';
import Axios from 'axios';
import { User } from '../types/ViewModel/User';
import { useRef, useEffect, useState } from 'react';
import { useAuthStore } from '../context/AuthContext';
import { useAppSettings } from '../hooks/useAppSettings';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../app/useToast';

// Create a singleton MSAL instance to prevent multiple instances with the same client ID
let msalInstance: Msal.PublicClientApplication | null = null;
let msalInitializationPromise: Promise<void> | null = null;
let isGloballyInitialized = false;

const getMsalInstance = (): Msal.PublicClientApplication => {
    if (!msalInstance) {
        msalInstance = new Msal.PublicClientApplication(AuthConfig);
    }
    return msalInstance;
};

const initializeMsalSingleton = async (): Promise<void> => {
    if (isGloballyInitialized) {
        return Promise.resolve();
    }

    if (msalInitializationPromise) {
        return msalInitializationPromise;
    }

    msalInitializationPromise = (async () => {
        try {
            const instance = getMsalInstance();
            await instance.initialize();
            isGloballyInitialized = true;
        } catch (error) {
            console.error('MSAL initialization failed:', error);
            msalInitializationPromise = null; // Reset for retry
            throw error;
        }
    })();

    return msalInitializationPromise;
};

export default () => {
    const { setLoggedInUser, loggedInUser, setUserIsUpdated, isAuthenticating, setIsAuthenticating, setIsVerifyingToken, setToken } = useAuthStore();
    const navigate = useNavigate();
    const { toastError } = useToast();
    const myMsalObj = getMsalInstance();
    const [isInitialized, setIsInitialized] = useState(false);
    const { settings: appSettings } = useAppSettings();
    // Use a ref to track authentication state that can be updated immediately
    const isAuthenticatingRef = useRef(isAuthenticating);

    // Initialize MSAL instance
    useEffect(() => {
        const handleInitialization = async () => {
            try {
                await initializeMsalSingleton();
                setIsInitialized(true);
            } catch (error) {
                console.error('MSAL initialization failed in hook:', error);
                // Retry initialization after a delay
                setTimeout(() => {
                    console.log('Retrying MSAL initialization...');
                    handleInitialization();
                }, 2000);
            }
        };

        if (isGloballyInitialized) {
            setIsInitialized(true);
        } else {
            handleInitialization();
        }
    }, []);

    // Update the ref when the state changes
    const updateAuthenticatingState = (newValue: boolean) => {
        isAuthenticatingRef.current = newValue;
        setIsAuthenticating(newValue);
    };

    const acquireToken = async (authObject: any): Promise<Msal.AuthenticationResult | null> => {
        if (!isInitialized) {
            return null;
        }

        try {
            // Always handle redirect promise first to capture auth response
            const redirectResult = await myMsalObj.handleRedirectPromise();
            if (redirectResult) {
                console.log('Token acquired from redirect response');
                setToken(redirectResult.accessToken);
                return redirectResult;
            }

            const accounts = myMsalObj.getAllAccounts();
            if (accounts.length === 0) {
                // No cached accounts, need interaction
                console.log('No accounts found, triggering login redirect');
                throw new Msal.InteractionRequiredAuthError('No accounts available');
            }

            // Try silent token acquisition first with the provided account
            try {
                const silentRequest = {
                    account: authObject.account || accounts[0],
                    scopes: LoginRequest.scopes,
                    authority: AuthConfig.auth.authority,
                };
                console.log('Attempting silent token acquisition');
                const silentResult = await myMsalObj.acquireTokenSilent(silentRequest);
                console.log('Silent token acquired successfully');
                setToken(silentResult.accessToken);
                return silentResult;
            } catch (silentError: any) {
                // If silent acquisition fails due to interaction required, trigger redirect
                if (silentError instanceof Msal.InteractionRequiredAuthError) {
                    console.warn('Silent token acquisition failed - interaction required');
                    throw silentError;
                }

                // For other errors, try to use what we have or fail gracefully
                console.warn('Silent token acquisition failed with error:', silentError);
                throw silentError;
            }
        } catch (error) {
            console.error('Token acquisition error:', error);

            // For InteractionRequiredAuthError, we need user to authenticate
            if (error instanceof Msal.InteractionRequiredAuthError) {
                console.warn('User interaction required for authentication');

                // Prevent infinite loops by checking if we're already in a redirect flow
                if (isAuthenticatingRef.current) {
                    console.warn('Already in authentication flow, preventing duplicate redirect');
                    return null;
                }

                // Check if we're in an iframe context to avoid unsafe navigation errors
                if (window.self !== window.top) {
                    console.warn('Cannot perform redirect from iframe context');
                    throw new Error('Authentication required but cannot redirect from iframe context');
                }

                try {
                    updateAuthenticatingState(true);
                    // Use popup instead of redirect to avoid iframe issues (like OrcaWebsite)
                    const result = await myMsalObj.acquireTokenPopup({
                        account: authObject.account || myMsalObj.getActiveAccount(),
                        scopes: LoginRequest.scopes,
                        authority: AuthConfig.auth.authority,
                    });
                    updateAuthenticatingState(false);
                    return result;
                } catch (popupError) {
                    console.error('acquireTokenPopup failed:', popupError);
                    updateAuthenticatingState(false);

                    // If popup fails, fall back to redirect only if not in iframe
                    if (window.self === window.top) {
                        try {
                            await myMsalObj.acquireTokenRedirect({
                                account: authObject.account || myMsalObj.getActiveAccount(),
                                scopes: LoginRequest.scopes,
                                authority: AuthConfig.auth.authority,
                            });
                            return null; // Redirect will handle the response
                        } catch (redirectError) {
                            console.error('acquireTokenRedirect failed:', redirectError);
                            throw redirectError;
                        }
                    }
                    throw popupError;
                }
            }

            // For other errors, return null and let caller handle it
            console.error('Token acquisition failed:', error);
            throw error;
        }
    };

    const interceptAxiosRequest = (account: Msal.AccountInfo) => {
        Axios.interceptors.request.use(async (config) => {
            try {
                const authResult = await acquireToken({
                    account,
                    scopes: LoginRequest.scopes,
                    authority: AuthConfig.auth.authority,
                });
                if (authResult && authResult.accessToken && authResult.accessToken.length > 0) {
                    config.headers.Authorization = 'Bearer ' + authResult.accessToken;
                    setToken(authResult.accessToken);
                } else {
                    console.error('Failed to acquire access token - no token returned');
                    // Redirect to login if token acquisition fails
                    setTimeout(() => {
                        window.location.assign('/login');
                    }, 100);
                    throw new Error('Failed to acquire access token.');
                }
            } catch (error) {
                console.error('Failed to acquire token for request:', error);

                // On any token acquisition error, redirect to login
                setTimeout(() => {
                    window.location.assign('/login');
                }, 100);
                throw error;
            }

            if (!config?.headers?.Authorization || (config.headers.Authorization as string).length < 10) {
                console.error('no authorization header');
                throw new Error('Failed to inject token into authorization header.');
            }
            config.params = { ...config.params, releaseId: appSettings?.releaseId || '' };
            return config;
        });
    };

    const interceptAxiosRequestWithAppAccessToken = (appAccessToken: string) => {
        Axios.interceptors.request.use(async (config) => {
            config.headers.Authorization = 'Bearer ' + appAccessToken;
            config.params = { ...config.params, releaseId: appSettings?.releaseId || '' };
            return config;
        });
    };

    const interceptAxiosResponseWithAppAccessToken = () => {
        Axios.interceptors.response.use(
            (response) => response,
            (error) => {
                if (error.response.status === 401) {
                    setLoggedInUser(null);
                    setTimeout(() => navigate('/login'), 0);
                }
                return error;
            }
        );
    };

    const checkTokenAndSetUser = async (authenticationResult: Msal.AuthenticationResult) => {
        try {
            interceptAxiosRequest(authenticationResult.account);
            setUser(authenticationResult);
        } catch (error) {
            console.error('checkTokenAndSetUser error:', error);
        }
    };
    const loginAzAd = () => {
        // Check the ref value which is always up-to-date
        if (isAuthenticatingRef.current || !isInitialized) return;

        // Check if we're in an iframe context
        if (window.self !== window.top) {
            console.warn('Cannot perform login redirect from iframe context, redirecting parent window');
            window.top?.location.assign('/login');
            return;
        }

        updateAuthenticatingState(true);
        myMsalObj
            .loginRedirect()
            .then(() => {
                updateAuthenticatingState(false);
            })
            .catch((error) => {
                console.error('Login redirect failed:', error);
                updateAuthenticatingState(false);

                // If redirect fails, try popup as fallback
                if (error.message?.includes('iframe') || error.message?.includes('navigation')) {
                    console.log('Attempting login popup as fallback...');
                    myMsalObj
                        .loginPopup()
                        .then(() => {
                            updateAuthenticatingState(false);
                        })
                        .catch((popupError) => {
                            console.error('Login popup also failed:', popupError);
                            updateAuthenticatingState(false);
                            toastError('Login failed. Please try refreshing the page.', 5000);
                        });
                }
            });
    };
    const logoutAzAd = () => {
        if (!isInitialized) {
            return;
        }
        if (loggedInUser) {
            myMsalObj
                .logoutRedirect({
                    account: loggedInUser.userName ? myMsalObj.getAccountByUsername(loggedInUser.userName) : null,
                    postLogoutRedirectUri: '/login',
                })
                .catch((error) => {
                    console.error('logout error ', error);
                });
        } else if (myMsalObj.getActiveAccount()) {
            myMsalObj
                .logoutRedirect({
                    account: myMsalObj.getActiveAccount(),
                    postLogoutRedirectUri: '/login',
                })
                .catch((error) => {
                    console.error('logout error ', error);
                });
        } else {
            myMsalObj
                .logoutRedirect({
                    account: myMsalObj.getAllAccounts()[0],
                    postLogoutRedirectUri: '/login',
                })
                .catch((error) => {
                    console.error('logout error ', error);
                });
        }
    };

    // Ensure isVerifyingToken is set to false if MSAL fails to initialize
    useEffect(() => {
        if (!isInitialized) {
            const timeout = setTimeout(() => {
                console.warn('MSAL initialization timeout, setting isVerifyingToken to false');
                setIsVerifyingToken(false);
            }, 5000); // Reduced from 10 to 5 seconds

            return () => clearTimeout(timeout);
        } else {
            // Also add a safety timeout for authentication verification
            const authTimeout = setTimeout(() => {
                console.warn('Authentication verification timeout, setting isVerifyingToken to false');
                setIsVerifyingToken(false);
            }, 10000); // 10 seconds for auth verification

            return () => clearTimeout(authTimeout);
        }
    }, [isInitialized, setIsVerifyingToken]);

    const checkAuthStatus = async () => {
        console.log('checkAuthStatus called, isInitialized:', isInitialized);
        if (!isInitialized) {
            setIsVerifyingToken(false);
            return null;
        }

        setIsVerifyingToken(true);

        try {
            // First, check if we're coming back from an Azure AD redirect
            const redirectResult = await myMsalObj.handleRedirectPromise();

            if (redirectResult) {
                // We successfully got a token from Azure AD redirect
                console.log('Got token from redirect:', redirectResult.account?.username);
                setToken(redirectResult.accessToken);
                await checkTokenAndSetUser(redirectResult);
                setIsVerifyingToken(false);
                return redirectResult;
            }

            // Check for an active or cached account
            let msalAccount = myMsalObj.getActiveAccount();
            if (!msalAccount) {
                const accounts = myMsalObj.getAllAccounts();
                if (accounts && accounts.length > 0) {
                    msalAccount = accounts[0];
                    myMsalObj.setActiveAccount(msalAccount);
                }
            }

            if (msalAccount && AuthConfig.auth) {
                // Like OrcaWebsite: Actually acquire a token to verify the account is still valid
                console.log('Found cached account, verifying token:', msalAccount.username);
                const authResult = await acquireToken({
                    account: msalAccount,
                    scopes: LoginRequest.scopes,
                    authority: AuthConfig.auth.authority,
                });

                if (authResult && authResult.accessToken && authResult.accessToken.length > 0) {
                    // Token acquired successfully - set up the user
                    console.log('Token verified successfully');
                    setToken(authResult.accessToken);
                    await checkTokenAndSetUser(authResult);
                } else {
                    // Token acquisition failed - log out and require fresh login
                    console.log('Token verification failed, logging out');
                    const signedInAccounts = myMsalObj.getAllAccounts();
                    if (signedInAccounts && signedInAccounts.length > 0) {
                        logoutAzAd();
                    }
                }
            } else {
                // No account found, redirect to login
                console.log('No cached account found, user needs to login');
                setLoggedInUser(null);
                loginAzAd();
            }

            setIsVerifyingToken(false);
            return null;
        } catch (error) {
            console.error('checkAuthStatus error:', error);
            setLoggedInUser(null);
            setIsVerifyingToken(false);
            return null;
        }
    };

    // Handle redirect promise on initialization (like OrcaWebsite's initHandleRedirect)
    const initHandleRedirect = () => {
        if (!isInitialized) {
            return;
        }
        myMsalObj
            .handleRedirectPromise()
            .then((result) => {
                if (result) {
                    checkTokenAndSetUser(result);
                }
            })
            .catch((error) => {
                console.error('msal handleRedirectPromise error:', error);
            });
    };

    const handleAppAccessToken = () => {
        const windowParams = new URLSearchParams(window.location.search);
        const appAccessTokenStr = windowParams.get('appAccessToken');
        const userIdStr = windowParams.get('userId') || 'AppUser';

        if (!appAccessTokenStr) return;

        interceptAxiosRequestWithAppAccessToken(appAccessTokenStr);
        interceptAxiosResponseWithAppAccessToken();
        const user = new User([], false, userIdStr, userIdStr, appAccessTokenStr);
        setLoggedInUser(user);
        setIsVerifyingToken(false);
        updateAuthenticatingState(false);
    };
    const setUser = (authenticationResult: Msal.AuthenticationResult) => {
        console.log('setUser called with:', authenticationResult);
        if (!isInitialized) {
            console.log('setUser: Not initialized, returning');
            return;
        }
        if (authenticationResult && authenticationResult.account) {
            console.log('setUser: Authentication result and account exist');
            const myAccount = authenticationResult.account.username
                ? myMsalObj.getAccountByUsername(authenticationResult.account.username)
                : authenticationResult.account;
            console.log('setUser: myAccount:', myAccount);
            myMsalObj.setActiveAccount(myAccount);
            if (myAccount) {
                const user = new User([], false, myAccount.name || myAccount.username || 'Unknown', myAccount.username || 'unknown');
                console.log('setUser: Created user:', user);
                setLoggedInUser(user);
                setUserIsUpdated(true);
                updateAuthenticatingState(false);
            } else {
                console.log('setUser: No account found, setting user to null');
                setLoggedInUser(null);
            }
        } else {
            console.log('setUser: No authentication result or account, setting user to null');
            setLoggedInUser(null);
            setUserIsUpdated(true);
        }
    };

    return {
        acquireToken,
        loginAzAd,
        logoutAzAd,
        isAuthenticating,
        msalInstance: myMsalObj,
        checkAuthStatus,
        initHandleRedirect,
        interceptAxiosRequestWithAppAccessToken,
        interceptAxiosResponseWithAppAccessToken,
        handleAppAccessToken,
        isInitialized,
    };
};
